/**
 * AI Trial Room (Phase 1) — Landing + 3 category tiles + Paywall
 * Uses MediaPipe/Three.js (added in Phase 2). Phase 1 = subscription/paywall + tile UI.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { Camera, Upload, Sparkles, Lock, CheckCircle2, X, ShoppingBag, Gem, Scissors, Store, Wallet } from 'lucide-react';
import api from '../../utils/api';
import TrialRoomLiveCamera from './TrialRoomLiveCamera';
import TrialRoomPhotoUpload from './TrialRoomPhotoUpload';
import TrialRoomShopFlow from './TrialRoomShopFlow';
import TrialRoomOwnerPanel from './TrialRoomOwnerPanel';
import TrialRoomLeaderboard from './TrialRoomLeaderboard';
import TrialRoomFaceFlow from './TrialRoomFaceFlow';
import { TrialRoomHistoryPanel, TrialRoomBatchPanel, TrialRoomImageGen } from './TrialRoomExtras';
import BuyATBalanceModal from '../../components/BuyATBalanceModal';

const CATEGORY_META = {
  apparels: { icon: ShoppingBag, gradient: 'from-fuchsia-500 via-rose-500 to-amber-500',   accent: 'fuchsia' },
  jewelry:  { icon: Gem,          gradient: 'from-amber-400 via-yellow-500 to-orange-500', accent: 'amber' },
  salon:    { icon: Scissors,     gradient: 'from-emerald-400 via-teal-500 to-cyan-500',    accent: 'emerald' },
};

const TrialRoom = () => {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [busy, setBusy] = useState('');
  const [shopFlow, setShopFlow] = useState(false);
  const [faceFlow, setFaceFlow] = useState(false);
  const [batchFlow, setBatchFlow] = useState(false);
  const [imgGen, setImgGen] = useState(false);
  const [showBuyAT, setShowBuyAT] = useState(false);
  const [atBalance, setAtBalance] = useState(null);
  // Live countdown ticker for time-based free trial + subscription expiry
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 1000); return () => clearInterval(t); }, []);
  const trCountdown = React.useMemo(() => {
    const exp = status?.subscription?.expires_at;
    if (!exp) return null;
    const ms = new Date(exp).getTime() - nowTick;
    if (ms <= 0) return { expired: true, dd: 0, hh: 0, mm: 0, ss: 0 };
    const s = Math.floor(ms / 1000);
    return { expired: false, dd: Math.floor(s / 86400), hh: Math.floor((s % 86400) / 3600), mm: Math.floor((s % 3600) / 60), ss: s % 60 };
  }, [status, nowTick]);
  const fmt2 = (n) => String(n).padStart(2, '0');
  // Full paywall gate — user has real access only if subscription active AND not expired
  const trialExpired = trCountdown && trCountdown.expired;
  const gatedNoAccess = !status?.has_access;
  const nav = useNavigate();

  const load = async () => {
    try {
      const [c, s] = await Promise.all([
        api.get('/trial-room/config'),
        api.get('/trial-room/status').catch(() => ({ data: null })),
      ]);
      setCfg(c.data);
      setStatus(s.data);
      // Fetch AT wallet balance (best-effort — silent if unauthenticated)
      try {
        const w = await api.get('/avx/wallet/me');
        setAtBalance(w.data?.wallet?.at_balance ?? null);
      } catch { /* not logged in */ }
    } catch { toast.error('Trial Room load failed'); }
  };
  useEffect(() => { load(); }, []);

  // Hard paywall: when trial expires OR user has no active subscription, auto-open paywall modal
  useEffect(() => {
    if (status && gatedNoAccess) setPaywall(true);
  }, [status, gatedNoAccess]);

  // 🔗 Handle admin-share unlock link (?assigned_batch=xxx) — show unlock toast on land
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const batchId = params.get('assigned_batch');
    if (!batchId) return;
    (async () => {
      try {
        const r = await api.get(`/trial-room/assigned-batch/${batchId}`);
        const cnt = r.data?.count || 0;
        if (cnt > 0) {
          toast.success(`🎁 Admin ne aapke liye ${cnt} naya item unlock kiya hai! Trial Room mein try karo.`, { duration: 7000 });
        }
      } catch (e) {
        const code = e.response?.status;
        if (code === 401 || code === 403) toast('🔒 Login karo — private items unlock karne ke liye.', { duration: 5000 });
        else if (code === 404) toast.error('Link expired ya aapke liye assigned nahi hai.', { duration: 5000 });
      }
      // Clean the URL param so refresh doesn't re-toast
      try {
        const clean = new URL(window.location.href);
        clean.searchParams.delete('assigned_batch');
        window.history.replaceState({}, '', clean.toString());
      } catch { /* noop */ }
    })();
  }, []);

  const subscribe = async (pkg) => {
    if (!window.confirm(`Subscribe to ${pkg.label}?\n\nAap ke AT wallet se ${pkg.amount} AT deduct hoga (67% Admin · 33% Affiliate Network 7-tier).`)) return;
    setBusy(pkg.id);
    try {
      const r = await api.post('/trial-room/subscribe', { package_id: pkg.id });
      toast.success(`🎉 SUCCESS! Subscription activated — ${pkg.label} · ${pkg.months} month(s)`);
      setStatus({ ...status, has_access: true, subscription: r.data.subscription });
      setPaywall(false);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      const code = e.response?.status;
      if (code === 402) toast.error(`❌ INSUFFICIENT FUNDS!\n${detail}`);
      else if (code === 401 || code === 403) toast.error('🔒 Login required');
      else toast.error(`❌ ${detail || 'Subscription failed'}`);
    } finally { setBusy(''); }
  };

  const buyPerTrial = async (qty) => {
    const cost = qty * (cfg?.pay_per_trial_cost_at || 10);
    if (!window.confirm(`Buy ${qty} pay-per-trial credit(s) for ${cost} AT?\n\n67% Admin · 33% Affiliate Network 7-tier upline commission.`)) return;
    setBusy(`ppt-${qty}`);
    try {
      const r = await api.post('/trial-room/pay-per-trial', { quantity: qty });
      toast.success(`🎉 ${r.data.trials_credited} trial credit(s) added! You now have ${r.data.free_trials_remaining} trials available.`);
      await load();
      setPaywall(false);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      const code = e.response?.status;
      if (code === 402) toast.error(`❌ INSUFFICIENT AT!\n${detail}`);
      else if (code === 401 || code === 403) toast.error('🔒 Login required');
      else toast.error(`❌ ${detail || 'Purchase failed'}`);
    } finally { setBusy(''); }
  };

  const buyCreditPack = async (pack) => {
    if (!window.confirm(`Buy ${pack.label} for ${pack.amount} AT?\n\n${pack.trials} trials · valid ${pack.validity_days} days · 67% Admin · 33% Affiliate Network 7-tier.`)) return;
    setBusy(`pack-${pack.id}`);
    try {
      const r = await api.post('/trial-room/buy-credit-pack', { pack_id: pack.id });
      toast.success(`🎉 ${pack.label} activated! ${pack.trials} trials · expires in ${pack.validity_days} days.`);
      await load();
      setPaywall(false);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      const code = e.response?.status;
      if (code === 402) toast.error(`❌ INSUFFICIENT AT!\n${detail}`);
      else if (code === 401 || code === 403) toast.error('🔒 Login required');
      else toast.error(`❌ ${detail || 'Purchase failed'}`);
    } finally { setBusy(''); }
  };

  const downloadReferralCard = async () => {
    try {
      const res = await api.get('/trial-room/my-referral-card', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `anant-referral-card.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('📥 Referral card downloaded! Share on WhatsApp/Insta.');
    } catch { toast.error('Referral card download failed. Login required?'); }
  };

  const openCategory = (cat) => {
    if (!status?.has_access) {
      setPaywall(true);
      return;
    }
    setSelected(cat);
  };

  if (!cfg) return <div className="min-h-screen pt-24 text-center text-slate-400">Loading AI Trial Room...</div>;

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-gradient-to-br from-slate-950 via-fuchsia-950/40 to-slate-950 text-white" data-testid="trial-room-page">
      <Helmet>
        <title>Anant AI Trial Room · Virtual Try-On · Apparels · Jewelry · Salon</title>
        <meta name="description" content="Anant AI Trial Room — virtual try-on for apparels, jewelry, and salon. Powered by client-side MediaPipe (browser-based). 7-day free trial." />
      </Helmet>

      <div className="max-w-6xl mx-auto space-y-6">
        {/* HERO */}
        <div className="relative rounded-3xl border-2 border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-900/50 via-indigo-900/40 to-emerald-900/30 p-6 md:p-8 overflow-hidden">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-300 font-black mb-1">॥ Anant AI · Vision Edition ॥</div>
              <h1 className="text-3xl md:text-5xl font-black bg-gradient-to-r from-fuchsia-300 via-amber-200 to-emerald-300 bg-clip-text text-transparent leading-tight">
                🪞 AI Trial Room
              </h1>
              <p className="text-sm md:text-base text-slate-200 mt-2 max-w-2xl">
                Virtual try-on powered by <b className="text-fuchsia-300">MediaPipe Vision AI</b> — runs 100% in your browser. Try on jewelry, hairstyles, makeup live, or upload a photo to see apparels.
              </p>
              <div className="flex gap-2 mt-3 flex-wrap">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 font-bold">✓ 2 FREE TRIALS</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/40 font-bold">✓ NO DOWNLOAD</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-400/40 font-bold">✓ PRIVACY-FIRST · CLIENT-SIDE</span>
              </div>
            </div>
            {/* Access badge */}
            <div className="flex flex-col items-end gap-2 min-w-[180px]">
              {/* 💰 AT Wallet display + Buy AT button (top-right, like other tools) */}
              <div className="flex items-center gap-2" data-testid="trial-room-at-wallet">
                {atBalance !== null && (
                  <div className="rounded-lg bg-black/60 border border-amber-400/40 px-2.5 py-1 flex items-center gap-1.5" title="Your AT Wallet balance">
                    <Wallet className="w-3.5 h-3.5 text-amber-300" />
                    <span className="text-xs font-black text-amber-300 font-mono" data-testid="trial-at-balance">{Number(atBalance).toFixed(2)}</span>
                    <span className="text-[9px] text-amber-200/70 font-black uppercase tracking-widest">AT</span>
                  </div>
                )}
                <button
                  onClick={() => setShowBuyAT(true)}
                  data-testid="trial-buy-at-btn"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-black bg-gradient-to-r from-amber-500 to-orange-500 text-black hover:scale-105 transition"
                >
                  <Sparkles className="w-3 h-3" /> Buy AT
                </button>
              </div>
              <div className="rounded-xl border-2 border-white/10 bg-black/40 p-3 text-right min-w-[180px] w-full">
              {status?.subscription && status?.subscription?.package_id !== 'free_trial_2d' && !trialExpired ? (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">✓ Premium Access</div>
                  <div className="text-sm font-black text-white mt-0.5">{status.subscription.package_label}</div>
                  {trCountdown && !trCountdown.expired && (
                    <div className="text-[11px] text-emerald-200 font-mono mt-0.5" data-testid="tr-countdown-paid">{fmt2(trCountdown.dd)}d : {fmt2(trCountdown.hh)}h : {fmt2(trCountdown.mm)}m : {fmt2(trCountdown.ss)}s</div>
                  )}
                </>
              ) : status?.subscription?.package_id === 'free_trial_2d' && !trialExpired ? (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">🆓 FREE TRIAL · 2 DAYS</div>
                  <div className="text-lg font-mono font-black text-cyan-200 mt-0.5" data-testid="tr-countdown-trial">
                    {trCountdown ? `${fmt2(trCountdown.dd)}d:${fmt2(trCountdown.hh)}h:${fmt2(trCountdown.mm)}m:${fmt2(trCountdown.ss)}s` : '—'}
                  </div>
                  <button data-testid="trial-upgrade-btn" onClick={() => setPaywall(true)} className="mt-1 text-[10px] px-2 py-0.5 rounded bg-amber-500 text-black font-black hover:bg-amber-400">Upgrade to Premium →</button>
                </>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-red-300 font-black">⚠ {trialExpired ? 'TRIAL EXPIRED' : 'ACCESS LOCKED'}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Subscribe to unlock AI Trial Room</div>
                  <button data-testid="trial-subscribe-btn" onClick={() => setPaywall(true)} className="mt-1 text-xs px-3 py-1 rounded bg-red-500 hover:bg-red-400 text-white font-black">Subscribe →</button>
                </>
              )}
            </div>
            </div>
          </div>
        </div>

        {/* 🎁 VIRAL REFERRAL LOOP — "Refer friends who subscribe = +1 bonus trial each" */}
        {status && (
          <ReferralBonusBanner status={status} />
        )}

        {/* 🎯 MY ACTIVE CREDIT PACKS — timers */}
        {status?.has_access !== undefined && <MyCreditPacksPanel />}

        {/* 🏆 TOP REFERRERS PUBLIC LEADERBOARD — shame/pride viral booster */}
        <TopReferrersLeaderboard />

        {/* 🎨 ANANT BRANDMARK — MOVED TO TOP (first tool card) */}
        <Link
          to="/brandmark"
          data-testid="trial-brandmark-btn"
          className="block relative rounded-2xl border-2 border-amber-400/60 bg-gradient-to-r from-amber-950 via-rose-900 to-amber-950 p-4 hover:scale-[1.005] transition-all shadow-lg overflow-hidden group"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.35),transparent_60%)] rounded-2xl" />
          <div className="relative z-10 flex items-center gap-3 flex-wrap">
            <div className="text-3xl">🎨</div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-[9px] uppercase tracking-widest text-amber-200 font-black">⭐ FEATURED · ANANT BRANDMARK · Viral Shop Marketing</div>
              <h4 className="text-base font-black text-white mt-0.5" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>Customer photo → Gold/Rose Gold branded shop advert</h4>
              <p className="text-[11px] text-white/85 mt-1">Auto-QR · Auto-viral caption · Shop logo overlay · 10 Indian languages · <b className="text-amber-200">300 AT/mo · 3000 AT/yr</b></p>
            </div>
            <div className="text-[10px] font-black text-amber-200 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Launch →</div>
          </div>
        </Link>

        {/* CATEGORY TILES */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {cfg.categories.map((cat) => {
            const meta = CATEGORY_META[cat.id] || CATEGORY_META.apparels;
            const Icon = meta.icon;
            return (
              <button
                key={cat.id}
                onClick={() => openCategory(cat)}
                data-testid={`trial-cat-${cat.id}`}
                className={`group relative rounded-2xl border-2 border-white/10 bg-gradient-to-br ${meta.gradient} p-5 text-left hover:scale-[1.02] transition-all shadow-lg`}
              >
                <div className="absolute inset-0 bg-black/40 rounded-2xl" />
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <Icon className="w-8 h-8 text-white drop-shadow-lg" />
                    {cat.mode === 'camera' ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-black/50 text-emerald-300 border border-emerald-400/40 font-black flex items-center gap-1"><Camera className="w-3 h-3" /> LIVE CAMERA</span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-black/50 text-fuchsia-300 border border-fuchsia-400/40 font-black flex items-center gap-1"><Upload className="w-3 h-3" /> PHOTO UPLOAD</span>
                    )}
                  </div>
                  <h3 className="text-lg font-black text-white leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>{cat.title}</h3>
                  <p className="text-xs text-white/85 mt-1">{cat.desc}</p>
                  <div className="mt-3 flex items-center gap-1 text-xs font-black text-white/90">
                    {!status?.has_access ? <><Lock className="w-3 h-3" /> Unlock</> : <><Sparkles className="w-3 h-3" /> Open →</>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* 🛍️ SHOP OWNER 2-CAMERA TRY-ON — flagship flow */}
        <button
          onClick={() => (status?.has_access ? setShopFlow(true) : setPaywall(true))}
          data-testid="trial-shop-owner-btn"
          className="w-full relative rounded-2xl border-2 border-amber-400/60 bg-gradient-to-r from-fuchsia-600 via-rose-600 to-amber-500 p-5 text-left hover:scale-[1.01] transition-all shadow-xl overflow-hidden group"
        >
          <div className="absolute inset-0 bg-black/35 rounded-2xl" />
          <div className="relative z-10 flex items-center gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-2xl bg-black/40 border-2 border-amber-300 flex items-center justify-center flex-shrink-0">
              <Store className="w-7 h-7 text-amber-200 drop-shadow" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-400 text-black font-black uppercase tracking-widest">🌟 FLAGSHIP</span>
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-black/50 text-emerald-300 border border-emerald-400/50 font-black">SHOP OWNER MODE</span>
              </div>
              <h3 className="text-xl md:text-2xl font-black text-white mt-1 leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                🛍️ 2-Camera Custom Try-On
              </h3>
              <p className="text-xs md:text-sm text-white/95 mt-0.5">
                Click customer photo + click apparel photo → AI merge with Male/Female/Boy/Girl · Download & WhatsApp share
              </p>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="text-[10px] font-black text-amber-200 flex items-center gap-1">
                {!status?.has_access ? <><Lock className="w-3 h-3" /> Unlock</> : <><Sparkles className="w-3 h-3" /> Launch →</>}
              </div>
            </div>
          </div>
        </button>

        {/* 💄 FACE AI TRY-ON · Batch · Image Generator tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            onClick={() => (status?.has_access ? setFaceFlow(true) : setPaywall(true))}
            data-testid="trial-face-btn"
            className="relative rounded-2xl border-2 border-rose-400/50 bg-gradient-to-br from-rose-600 via-pink-600 to-fuchsia-600 p-4 text-left hover:scale-[1.01] transition-all shadow-lg overflow-hidden group">
            <div className="absolute inset-0 bg-black/30 rounded-2xl" />
            <div className="relative z-10">
              <div className="text-3xl mb-1">💄</div>
              <div className="text-[9px] uppercase tracking-widest text-rose-200 font-black">FACE AI TRY-ON</div>
              <h4 className="text-base font-black text-white mt-0.5" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>Cosmetics · Glasses · Lens</h4>
              <p className="text-[11px] text-white/90 mt-1">Lipstick · Blush · Eyeshadow · Foundation · Sunglasses · Cap · Contact Lens · Earrings · Necklace · Beard</p>
              <div className="mt-2 text-[10px] font-black text-rose-100 flex items-center gap-1">
                {!status?.has_access ? <><Lock className="w-3 h-3" /> Unlock</> : <><Sparkles className="w-3 h-3" /> Launch →</>}
              </div>
            </div>
          </button>

          <button
            onClick={() => (status?.has_access ? setBatchFlow(true) : setPaywall(true))}
            data-testid="trial-batch-btn"
            className="relative rounded-2xl border-2 border-cyan-400/50 bg-gradient-to-br from-cyan-600 via-blue-600 to-indigo-600 p-4 text-left hover:scale-[1.01] transition-all shadow-lg overflow-hidden group">
            <div className="absolute inset-0 bg-black/30 rounded-2xl" />
            <div className="relative z-10">
              <div className="text-3xl mb-1">📦</div>
              <div className="text-[9px] uppercase tracking-widest text-cyan-200 font-black">BATCH TRY-ON</div>
              <h4 className="text-base font-black text-white mt-0.5" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>1 Customer × 5 Apparels</h4>
              <p className="text-[11px] text-white/90 mt-1">Parallel AI processing — customer ko 5 alag dresses me ek saath dikhao. Best for shop consultations.</p>
              <div className="mt-2 text-[10px] font-black text-cyan-100 flex items-center gap-1">
                {!status?.has_access ? <><Lock className="w-3 h-3" /> Unlock</> : <><Sparkles className="w-3 h-3" /> Launch →</>}
              </div>
            </div>
          </button>

          <button
            onClick={() => (status?.has_access ? setImgGen(true) : setPaywall(true))}
            data-testid="trial-imgen-btn"
            className="relative rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-500 via-orange-600 to-rose-600 p-4 text-left hover:scale-[1.01] transition-all shadow-lg overflow-hidden group">
            <div className="absolute inset-0 bg-black/30 rounded-2xl" />
            <div className="relative z-10">
              <div className="text-3xl mb-1">🎨</div>
              <div className="text-[9px] uppercase tracking-widest text-amber-200 font-black">AI IMAGE GENERATOR</div>
              <h4 className="text-base font-black text-white mt-0.5" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>Text → Image (Free-form)</h4>
              <p className="text-[11px] text-white/90 mt-1">Prompt likho → banner, poster, product mock-up ya kuch bhi banao Nano Banana se.</p>
              <div className="mt-2 text-[10px] font-black text-amber-100 flex items-center gap-1">
                {!status?.has_access ? <><Lock className="w-3 h-3" /> Unlock</> : <><Sparkles className="w-3 h-3" /> Launch →</>}
              </div>
            </div>
          </button>
        </div>

        {/* 📚 History Panel */}
        {status?.has_access && <TrialRoomHistoryPanel />}

        {/* 🏆 PUBLIC LEADERBOARD — viral competition (visible to all) */}
        <TrialRoomLeaderboard />

        {/* 🏪 SHOP OWNER PANEL — Per-user branding + private analytics (only for authenticated users) */}
        {status?.has_access && <TrialRoomOwnerPanel />}

        {/* PHOTO UPLOAD MODE — MediaPipe Pose (Phase 3) */}
        {selected && selected.mode === 'photo' && (
          <TrialRoomPhotoUpload
            category={selected.id}
            categoryTitle={selected.title}
            onClose={() => setSelected(null)}
          />
        )}

        {/* LIVE CAMERA MODE — MediaPipe FaceMesh (Phase 2) */}
        {selected && selected.mode === 'camera' && (
          <TrialRoomLiveCamera
            category={selected.id}
            categoryTitle={selected.title}
            onClose={() => setSelected(null)}
          />
        )}

        {/* 🛍️ SHOP OWNER · 2-CAMERA FLOW */}
        {shopFlow && <TrialRoomShopFlow onClose={() => setShopFlow(false)} />}

        {/* 💄 FACE AI TRY-ON */}
        {faceFlow && <TrialRoomFaceFlow onClose={() => setFaceFlow(false)} />}

        {/* 📦 BATCH TRY-ON */}
        {batchFlow && <TrialRoomBatchPanel onClose={() => setBatchFlow(false)} />}

        {/* 🎨 IMAGE GENERATOR */}
        {imgGen && <TrialRoomImageGen onClose={() => setImgGen(false)} />}

        <BuyATBalanceModal open={showBuyAT} onClose={() => { setShowBuyAT(false); load(); }} />

        {/* Fund split note */}
        <div className="rounded-xl border border-white/10 bg-black/30 p-3 text-[11px] text-slate-400 text-center">
          Every subscription contributes to the ecosystem: <b className="text-fuchsia-300">67%</b> Platform Development · <b className="text-emerald-300">33%</b> 7-Tier Affiliate Network Rewards to your upline (10/7/5/4/3/2/2%)
        </div>

        {/* Privacy Trust Badge */}
        <div className="rounded-xl border-2 border-emerald-400/40 bg-gradient-to-r from-emerald-950/60 via-slate-950/80 to-cyan-950/60 p-3 md:p-4" data-testid="trial-privacy-badge">
          <div className="flex items-center justify-center gap-3 flex-wrap text-center">
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 border-2 border-emerald-400/60 flex items-center justify-center text-lg">🔒</div>
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">Privacy-First</div>
                <div className="text-[10px] text-slate-300">Photos never leave your device</div>
              </div>
            </div>
            <div className="hidden md:block w-px h-8 bg-white/10" />
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-8 rounded-full bg-cyan-500/20 border-2 border-cyan-400/60 flex items-center justify-center text-lg">🌐</div>
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">Powered by MediaPipe</div>
                <div className="text-[10px] text-slate-300">Google&apos;s open-source vision AI</div>
              </div>
            </div>
            <div className="hidden md:block w-px h-8 bg-white/10" />
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 border-2 border-amber-400/60 flex items-center justify-center text-lg">⚡</div>
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">100% Browser-Side</div>
                <div className="text-[10px] text-slate-300">Zero cloud upload · Instant results</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PAYWALL MODAL */}
      {paywall && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setPaywall(false)} data-testid="trial-paywall-modal">
          <div className="max-w-3xl w-full rounded-2xl border-2 border-fuchsia-400/60 bg-gradient-to-br from-slate-950 via-fuchsia-950/60 to-indigo-950 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black">॥ Choose Your Pass ॥</div>
                <h2 className="text-2xl font-black text-white mt-1">🪞 Unlock AI Trial Room</h2>
                <p className="text-xs text-slate-300 mt-0.5">
                  Free = <b className="text-amber-300">{cfg.free_lifetime_trials || 2} lifetime trials only</b>.
                  Paid = <b className="text-emerald-300">{cfg.paid_daily_limit || 10}/day &amp; {cfg.paid_monthly_limit || 300}/month</b>.
                </p>
              </div>
              <button onClick={() => setPaywall(false)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {cfg.packages.map((pkg) => (
                <div key={pkg.id} className={`relative rounded-xl border-2 p-3 flex flex-col ${pkg.highlight ? 'border-amber-400 ring-2 ring-amber-400/40' : 'border-white/10'} bg-gradient-to-br ${pkg.color}`}>
                  {pkg.highlight && <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] px-2 py-0.5 rounded-full bg-amber-400 text-black font-black">🌟 POPULAR</span>}
                  <div className="text-sm font-black text-white">{pkg.label}</div>
                  <div className="text-3xl font-black text-white mt-1">{pkg.amount}<span className="text-xs opacity-80 ml-1">AT</span></div>
                  <div className="text-[10px] text-white/90 mt-1 flex-1">{pkg.desc}</div>
                  <div className="text-[10px] text-white/70 mt-1">{pkg.months} month{pkg.months > 1 ? 's' : ''} validity</div>
                  <button
                    onClick={() => subscribe(pkg)}
                    disabled={busy === pkg.id}
                    data-testid={`trial-pkg-${pkg.id}`}
                    className="mt-2 px-3 py-2 rounded-lg bg-black/50 hover:bg-black/70 text-white text-xs font-black flex items-center justify-center gap-1 disabled:opacity-50">
                    {busy === pkg.id ? '⏳ Processing...' : <><CheckCircle2 className="w-3 h-3" /> Subscribe</>}
                  </button>
                </div>
              ))}
            </div>

            {/* 💎 PAY-PER-TRIAL (a-la-carte) */}
            <div className="mt-3 rounded-xl border-2 border-cyan-400/50 bg-gradient-to-r from-cyan-600/20 via-teal-600/15 to-emerald-600/20 p-3" data-testid="trial-pay-per-trial-panel">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">⚡ PAY-PER-TRIAL</div>
                  <div className="text-lg font-black text-white">
                    <span className="text-cyan-300">{cfg.pay_per_trial_cost_at || 25} AT</span> per trial · <span className="text-amber-300">min {cfg.pay_per_trial_min_qty || 2} trials</span> per buy · repeatable
                  </div>
                  <div className="text-[10px] text-slate-300">Har trial pe aap ka <b className="text-amber-300">shop logo &amp; naam auto-watermark</b>. 33% Affiliate Network upline ko bhi milta hai.</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {[2, 5, 10, 20].map((q) => (
                    <button
                      key={q}
                      onClick={() => buyPerTrial(q)}
                      disabled={busy === `ppt-${q}`}
                      data-testid={`buy-per-trial-${q}`}
                      className="px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-xs font-black hover:scale-[1.02] transition disabled:opacity-50"
                    >
                      {busy === `ppt-${q}` ? '⏳' : `+${q} · ${q * (cfg.pay_per_trial_cost_at || 25)} AT`}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* 🎯 CREDIT PACKS — validity-based repeat-buy bundles */}
            {Array.isArray(cfg.credit_packs) && cfg.credit_packs.length > 0 && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="trial-credit-packs">
                {cfg.credit_packs.map((pk) => (
                  <div
                    key={pk.id}
                    className={`relative rounded-xl border-2 border-white/10 p-3 bg-gradient-to-br ${pk.color} overflow-hidden`}
                  >
                    <div className="absolute -top-2 left-3 text-[9px] px-2 py-0.5 rounded-full bg-black/60 text-amber-300 font-black uppercase tracking-widest">
                      🎯 Credit Pack · Repeat-buyable
                    </div>
                    <div className="flex items-start justify-between gap-2 mt-3">
                      <div>
                        <div className="text-lg font-black text-white">{pk.label}</div>
                        <div className="text-[11px] text-white/95 mt-0.5">{pk.desc}</div>
                        <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px] font-black">
                          <span className="px-1.5 py-0.5 rounded bg-black/40 text-emerald-300">{pk.trials} Trials</span>
                          <span className="px-1.5 py-0.5 rounded bg-black/40 text-cyan-300">Valid {pk.validity_days}d</span>
                          <span className="px-1.5 py-0.5 rounded bg-black/40 text-amber-300">Own Branding</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-3xl font-black text-white leading-none">{pk.amount}</div>
                        <div className="text-[10px] text-white/80 font-black">AT</div>
                      </div>
                    </div>
                    <button
                      onClick={() => buyCreditPack(pk)}
                      disabled={busy === `pack-${pk.id}`}
                      data-testid={`buy-credit-pack-${pk.id}`}
                      className="mt-3 w-full px-3 py-2 rounded-lg bg-black/50 hover:bg-black/70 text-white text-xs font-black flex items-center justify-center gap-1 disabled:opacity-50"
                    >
                      {busy === `pack-${pk.id}` ? '⏳ Processing...' : <>🎯 Buy {pk.label}</>}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* 🎨 BRANDMARK PLANS — separate section inside same modal */}
            <div className="mt-4 pt-3 border-t-2 border-amber-400/30" data-testid="trial-brandmark-plans-section">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">🎨 ANANT BRANDMARK · Separate Subscription</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Link to="/brandmark" data-testid="trial-brandmark-plan-monthly" className="rounded-xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-900/40 to-rose-900/30 p-3 hover:scale-[1.01]">
                  <div className="text-[9px] uppercase tracking-widest text-amber-200 opacity-80">Monthly</div>
                  <div className="text-2xl font-black text-white">300 <span className="text-xs opacity-80">AT</span></div>
                  <div className="text-[10px] text-amber-100">30 days · Viral shop marketing</div>
                  <div className="mt-1 text-[10px] font-black text-amber-200">→ Subscribe</div>
                </Link>
                <Link to="/brandmark" data-testid="trial-brandmark-plan-yearly" className="rounded-xl border-2 border-emerald-400/60 bg-gradient-to-br from-emerald-900/40 to-amber-900/30 p-3 hover:scale-[1.01]">
                  <div className="flex items-center justify-between"><div className="text-[9px] uppercase tracking-widest text-emerald-200 opacity-80">Yearly</div><div className="text-[8px] font-black px-1.5 py-0.5 rounded-full bg-emerald-500 text-black">BEST</div></div>
                  <div className="text-2xl font-black text-white">3,000 <span className="text-xs opacity-80">AT</span></div>
                  <div className="text-[10px] text-emerald-100">365 days · Save 17% · 2 months FREE</div>
                  <div className="mt-1 text-[10px] font-black text-emerald-200">→ Subscribe</div>
                </Link>
              </div>
              <div className="mt-1 text-[10px] text-slate-400">33% Affiliate Network (7-tier) · Same commission structure. Also unlocked FREE if you have any active Trial Room pass (COMBO).</div>
            </div>

            {cfg.custom_pack && (
              <a
                href={cfg.custom_pack.url}
                target="_blank" rel="noopener noreferrer"
                data-testid="trial-custom-pack-whatsapp"
                className="mt-3 flex items-center justify-between gap-3 rounded-xl border-2 border-emerald-400/50 bg-gradient-to-r from-emerald-600/30 via-emerald-500/20 to-teal-600/30 p-3 hover:scale-[1.005] transition-transform"
              >
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">💬 Custom Pack</div>
                  <div className="text-sm font-black text-white">{cfg.custom_pack.label}</div>
                  <div className="text-[10px] text-emerald-200">Contact admin: <b>{cfg.custom_pack.whatsapp}</b></div>
                </div>
                <span className="px-3 py-2 rounded-lg bg-emerald-500 text-black text-xs font-black shrink-0">WhatsApp →</span>
              </a>
            )}
            <div className="mt-3 text-[10px] text-slate-400 text-center">
              💡 67% goes to Platform Development · 33% distributed to 7-tier upline (10/7/5/4/3/2/2%) — same as ASST / Samadhan
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrialRoom;

// ─────────────────────────────────────────────────────────────
// Viral Referral Loop widget — shown on the Trial Room home
// Growth-safe: friend must subscribe to a paid pack for referrer to earn a bonus trial.
// ─────────────────────────────────────────────────────────────
const ReferralBonusBanner = ({ status }) => {
  const friends = Number(status?.friends_subscribed || 0);
  const earned  = Number(status?.bonus_trials_earned || 0);
  const isPaid  = Boolean(status?.subscription);
  // Build a WhatsApp share URL — copy referral code from window origin if we can
  const [refCode, setRefCode] = React.useState('');
  React.useEffect(() => {
    let cancel = false;
    api.get('/trial-room/referral-bonus')
      .then((r) => { if (!cancel) setRefCode(r.data?.referral_code || ''); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const shareUrl = refCode ? `${origin}/register?ref=${encodeURIComponent(refCode)}` : origin;
  const waText = encodeURIComponent(
    `Namaste 🙏 Main Anant AI Trial Room use kar raha hoon — apni photo pe kapde/makeup try kar sakte ho.\n` +
    `Aap bhi try karo, mere referral link se sign up karo:\n${shareUrl}\n\n` +
    `Jab aap subscribe karoge to mujhe +1 bonus trial milega — aur aapko flagship AI features (10/day) unlock 🎁`
  );
  const waHref = `https://wa.me/?text=${waText}`;

  return (
    <div
      className="rounded-2xl border-2 border-emerald-400/50 bg-gradient-to-r from-emerald-600/25 via-teal-600/20 to-fuchsia-600/25 p-4 md:p-5"
      data-testid="trial-referral-banner"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">🎁 VIRAL REFERRAL LOOP</div>
          <h3 className="text-lg md:text-xl font-black text-white mt-0.5">
            {isPaid ? 'Refer friends → Earn +1 bonus Trial for each' : 'Invite friends → Unlock bonus Trials 🎁'}
          </h3>
          <p className="text-[11px] md:text-xs text-slate-200 mt-1 leading-relaxed">
            Har wo dost jo aap ke referral code se sign-up karke <b className="text-emerald-300">paid Trial Room pack</b> lega — aap ko <b className="text-amber-300">+1 bonus Trial</b> milega.
            Zero cost to you, zero fake sign-up burn. Grow your team, grow your credits. 🌱
          </p>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <div className="rounded-lg bg-black/40 border border-white/10 px-2 py-1">
              <div className="text-[9px] uppercase text-slate-400 font-black">Friends Subscribed</div>
              <div className="text-xl font-black text-emerald-300" data-testid="referral-friends-count">{friends}</div>
            </div>
            <div className="rounded-lg bg-black/40 border border-white/10 px-2 py-1">
              <div className="text-[9px] uppercase text-slate-400 font-black">Bonus Trials Earned</div>
              <div className="text-xl font-black text-amber-300" data-testid="referral-bonus-earned">{earned}</div>
            </div>
            <div className="rounded-lg bg-black/40 border border-white/10 px-2 py-1">
              <div className="text-[9px] uppercase text-slate-400 font-black">Your Code</div>
              <div className="text-sm font-black text-fuchsia-300 font-mono" data-testid="referral-code">{refCode || '—'}</div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <a
            href={waHref}
            target="_blank" rel="noopener noreferrer"
            data-testid="referral-whatsapp-share"
            className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-black text-xs md:text-sm font-black flex items-center gap-2 shadow-lg hover:scale-[1.02] transition-transform"
          >
            📲 Share on WhatsApp
          </a>
          <button
            onClick={async () => {
              try {
                const res = await api.get('/trial-room/my-referral-card', { responseType: 'blob' });
                const blob = new Blob([res.data], { type: 'image/png' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `anant-referral-card.png`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success('📥 Referral card downloaded! Share on WhatsApp/Insta.');
              } catch { toast.error('Card download failed. Login required?'); }
            }}
            data-testid="referral-download-card"
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-amber-500 text-white text-[11px] font-black hover:scale-[1.02] transition"
          >
            🎴 Download Card (PNG)
          </button>
          <button
            onClick={() => {
              if (!shareUrl) return;
              navigator.clipboard?.writeText(shareUrl);
              toast.success('Referral link copied!');
            }}
            data-testid="referral-copy-link"
            className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-[11px] font-black hover:bg-white/20"
          >
            📋 Copy Link
          </button>
        </div>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────
// Top Referrers Public Leaderboard — shame/pride booster for viral growth
// Ranks users by number of PAID subscriptions they referred (this month by default).
// ─────────────────────────────────────────────────────────────
const TopReferrersLeaderboard = () => {
  const [period, setPeriod] = React.useState('month');
  const [leaders, setLeaders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [reward, setReward] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/trial-room/top-referrers?period=${period}&limit=10`);
      setLeaders(r.data?.leaders || []);
      setReward(r.data?.reward_note || '');
    } catch { setLeaders([]); }
    finally { setLoading(false); }
  }, [period]);
  React.useEffect(() => { load(); }, [load]);

  const medal = (i) => (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`);

  return (
    <div
      className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-900/25 via-orange-900/20 to-rose-900/25 p-4 md:p-5"
      data-testid="trial-top-referrers"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🏆 TOP REFERRERS · PUBLIC LEADERBOARD</div>
          <h3 className="text-lg md:text-xl font-black text-white mt-0.5">Kaun laa raha hai sabse zyada paid subscribers?</h3>
          <p className="text-[11px] text-slate-300 mt-0.5">{reward || 'Top-3 this month get 1-month FREE VIP Pass 🏆'}</p>
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-black/40 border border-white/10 p-0.5" data-testid="top-referrers-period-tabs">
          {['day', 'week', 'month', 'all'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              data-testid={`top-ref-tab-${p}`}
              className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest ${period === p ? 'bg-amber-500 text-black' : 'text-slate-300 hover:bg-white/5'}`}
            >{p}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400 text-xs text-center py-6">Loading leaderboard...</div>
      ) : leaders.length === 0 ? (
        <div className="text-slate-400 text-xs text-center py-6" data-testid="top-referrers-empty">
          Abhi tak koi paid referral nahi hui. <b className="text-amber-300">Aap #1 ban sakte ho!</b> — apna referral link WhatsApp pe share karo.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {leaders.map((l, i) => (
            <div
              key={l.referrer_id}
              data-testid={`top-referrer-row-${i}`}
              className={`flex items-center gap-3 rounded-xl border p-2.5 ${i < 3 ? 'bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-orange-500/20 border-amber-400/40' : 'bg-black/30 border-white/10'}`}
            >
              <div className={`text-2xl font-black w-9 text-center ${i === 0 ? 'text-amber-300' : i === 1 ? 'text-slate-300' : i === 2 ? 'text-orange-400' : 'text-slate-500'}`}>
                {medal(i)}
              </div>
              {l.shop_logo_url ? (
                <img src={l.shop_logo_url} alt="" className="w-9 h-9 rounded-full object-cover border border-white/20" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-fuchsia-500 to-amber-500 flex items-center justify-center text-white text-xs font-black">
                  {(l.display_name || l.referrer_username || '?').slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-black text-white truncate">
                  {l.shop_name || l.display_name}
                  <span className="text-[10px] text-slate-400 font-mono ml-1">@{l.referrer_username}</span>
                </div>
                <div className="text-[10px] text-slate-300">
                  <b className="text-emerald-300">{l.referrals}</b> paid referral{l.referrals !== 1 ? 's' : ''} · <b className="text-amber-300">{l.bonus_trials}</b> bonus trials earned
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// My Credit Packs Panel — validity-based bundle timers (60d/120d etc)
// ─────────────────────────────────────────────────────────────
const MyCreditPacksPanel = () => {
  const [packs, setPacks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/trial-room/my-credit-packs');
      setPacks(r.data?.packs || []);
    } catch { setPacks([]); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const active = packs.filter((p) => !p.expired && p.trials_remaining > 0);
  if (loading || active.length === 0) return null;

  return (
    <div
      className="rounded-2xl border-2 border-cyan-400/50 bg-gradient-to-br from-cyan-900/25 via-teal-900/20 to-fuchsia-900/20 p-4"
      data-testid="my-credit-packs-panel"
    >
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-2">🎯 MY ACTIVE CREDIT PACKS</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {active.map((p) => {
          const pct = Math.round(100 * (p.trials_used / Math.max(1, p.trials_total)));
          return (
            <div key={p.id} className="rounded-xl border border-white/10 bg-black/40 p-3" data-testid={`credit-pack-row-${p.pack_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-black text-white">{p.pack_label}</div>
                  <div className="text-[10px] text-slate-400 font-mono">Purchased: {new Date(p.purchased_at).toLocaleDateString('en-IN')}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-amber-300 font-black">⏳ {p.days_left}d left</div>
                  <div className="text-[10px] text-slate-400">Expires: {new Date(p.expires_at).toLocaleDateString('en-IN')}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 text-[11px]">
                <span className="text-emerald-300 font-black">{p.trials_remaining} of {p.trials_total} trials left</span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400" style={{ width: `${100 - pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

